export const DEBATE_ROLES = [
  {
    key: "advocate",
    title: "Affirmative Advocate",
    subtitle: "Builds the strongest version of your argument.",
    model: "ChatGPT",
    accent: "#10a37f",
    logo: "/openai-icon.svg",
  },
  {
    key: "critic",
    title: "Critical Analyst",
    subtitle: "Finds vulnerabilities opposing counsel will attack.",
    model: "Claude",
    accent: "#d97757",
    logoCdn: "claude",
  },
  {
    key: "strategist",
    title: "Strategic Analyst",
    subtitle: "Recommends timing, posture, and next steps.",
    model: "Gemini",
    accent: "#4285f4",
    logoCdn: "googlegemini",
  },
];

export const DEBATE_ROUNDS = [
  {
    round: 1,
    title: "Opening Arguments",
    showContinues: true,
    arguments: {
      advocate: {
        heading: "Opening — Substantial Performance",
        bullets: [
          "Substantial performance supports the defense where the buyer received the essential benefit of the bargain despite late delivery.",
          "Cure was commercially reasonable and completed within the agreed window — substantial performance defeats the breach claim on these facts.",
          "Continued acceptance after tender is strong supporting authority for waiver of strict timing requirements.",
        ],
      },
      critic: {
        heading: "Initial Weaknesses",
        bullets: [
          "Express time-of-essence language, if present, may foreclose the substantial performance defense under applicable governing law.",
          "No documented evidence that buyer's post-cure acceptance was affirmative — silence is not waiver in all jurisdictions.",
          "Cure tender alone is insufficient without a written record establishing timeliness and commercial reasonableness.",
        ],
      },
      strategist: {
        heading: "Procedural Posture",
        bullets: [
          "Forum and governing law are the first strategic levers — lock favorable jurisdiction before motions practice begins.",
          "Framing the dispute around acceptance conduct matters as much as legal authority at this stage.",
          "Sequence: resolve governing law first, then build the substantive cure defense on a stable doctrinal foundation.",
        ],
      },
    },
  },
  {
    round: 2,
    title: "Cross-Examination & Rebuttal",
    arguments: {
      advocate: {
        heading: "Reinforce — Cure Tender Doctrine",
        bullets: [
          "Cure tender was timely, in writing, and commercially reasonable on the facts.",
          "Buyer's silence following tender supports an inference of waiver of strict timing.",
          "Even in strict-timing jurisdictions, cure acted upon by the non-breaching party defeats materiality.",
        ],
      },
      critic: {
        heading: "Sharpen — Governing Law Risk",
        bullets: [
          "Goods-or-services classification is unsettled; the applicable framework may shift depending on contract characterization.",
          "Advocate has not addressed how time-of-essence language interacts with cure rights under governing law.",
          "Mitigation record is incomplete — damages exposure persists even if liability is defeated.",
        ],
      },
      strategist: {
        heading: "Tactical Sequencing",
        bullets: [
          "Clarify whether the contract is governed by goods, services, or mixed-contract principles before relying on cure doctrine.",
          "Move to bifurcate damages early — keeps the cure dispute clean on liability.",
          "If governing law classification is genuinely unsettled, move to clarify it early — do not let opposing counsel choose the more favorable framework by default.",
        ],
      },
    },
  },
  {
    round: 3,
    title: "Closing & Synthesis",
    arguments: {
      advocate: {
        heading: "Closing — Defense Theory Locked",
        bullets: [
          "Substantial performance plus cure tender defeats the material-breach element on the existing record.",
          "Waiver via continued acceptance is the cleanest path to summary judgment.",
          "Backup theories are available if governing law shifts the applicable standard.",
        ],
      },
      critic: {
        heading: "Closing — Residual Risks",
        bullets: [
          "If time-of-essence language survives, the defense weakens — backup theories must be pled in the alternative.",
          "Supporting authority is favorable at the appellate level, but trial-court precedent is thin.",
          "Mitigation documentation must be built contemporaneously or risk a damages reset on appeal.",
        ],
      },
      strategist: {
        heading: "Closing — Strategic Recommendations",
        bullets: [
          "Open with venue motion, anchor the brief in governing law, and preserve the alternate theory throughout.",
          "Plead estoppel in the alternative as a backstop against strict time-of-essence interpretation.",
          "Do not wait for opposing counsel to exploit the record gaps — lock forum, confirm governing law, and file alternative theories now.",
        ],
      },
    },
  },
];

export const FINAL_CONSENSUS = {
  strength: "STRONG",
  bullets: [
    "Key Consensus: All three roles agree the strongest defense is substantial performance plus timely cure tender, supported by buyer acceptance conduct after delivery.",
    "Main Vulnerability: Express time-of-essence language is the strongest attack point — if governing law treats strict timing as a material condition, the cure defense weakens substantially.",
    "Missing Proof: The record needs stronger documentation of cure timing, written acceptance communications, and mitigation efforts before dispositive motion practice.",
    "Recommended Next Step: Preserve alternative theories, confirm governing law and forum, and build the factual record now — before opposing counsel identifies the same gaps.",
  ],
};

export const COMPARISON_DASHBOARD = {
  improved:
    "Contractual acceptance framework strengthened after revision.",
  weak: "Time-of-essence interpretation remains a litigation risk.",
  risk: "Cure documentation still needs stronger record support.",
};
